/**
 * Banca Intesa.
 *
 * Intesa exports a card payment twice. On the day it is made the row is not
 * settled yet, and reads as the merchant wrote it. Once it settles, the export
 * carries the same payment again — on the day it settled, and with **your own
 * name appended to the description**. Same money, two dates, two descriptions,
 * and a same-day check sees two transactions.
 *
 * So the question "is this row settled" is not asked of the date at all. It is
 * asked of the row itself:
 *
 *   1. Is the note `PLAĆANJE KARTICOM`? If not, it is not a card payment and
 *      there is nothing to wait for.
 *   2. Does the description end in your surname? If it does, Intesa has already
 *      settled it. If it does not, it may still move.
 *
 * A cash withdrawal at an ATM (note `BANKOMAT`) waits the same way, and its fee
 * with it, but settles differently: the pending row names the machine ("Isplata
 * gotovine na ATM-u ATM POSTA 26910 PANCEVO"), the settled one has your name *in
 * place of* the machine. Taking the name off is not enough to pair them, so a
 * withdrawal is compared by its amount and what it is — the first word,
 * "Isplata", "Naknada" — and left to the window and the date to tell two apart.
 * Money paid *in* at an ATM carries no name either way and is never pending.
 *
 * Nobody tells us the surname, so it is read off the ledger: group every
 * description by its last word and the commonest one wins, because that name is
 * appended to every settled card payment you have ever made and nothing else
 * repeats like that.
 */
import { readStatement } from '../statement.js'

/** The note Intesa puts on a card payment, folded for comparison. */
export const CARD_NOTE = 'placanje karticom'

/** The note on anything done at an ATM: withdrawals, their fees, deposits. */
export const ATM_NOTE = 'bankomat'

const fold = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()

/** An entry carries `type`; a stored transaction carries `payment_type`. */
const noteOf = (row) => fold(row?.payment_type ?? row?.type ?? '')

const words = (description) => fold(description).split(/[^a-z0-9]+/).filter(Boolean)

/** How many times a trailing name must appear before it is believed. */
export const NAME_CONFIDENCE = 3

/** How many words of name to look for. "PETROVIC PETAR" is two; some are three. */
const MAX_NAME_WORDS = 3

/**
 * The name Intesa appends to a settled card payment, as far as the ledger can
 * tell: the run of words that ends more descriptions than any other.
 *
 * It starts from the commonest last word, then walks left for as long as the
 * longer ending is still nearly as common. That step matters, because Intesa
 * appends your *full* name: the commonest last word is only half of it, and
 * taking off that half would leave the other half behind, so the two halves of
 * a pair still would not match.
 *
 * Returns null when nothing stands out - a ledger too new to have settled
 * anything, or one where no ending repeats. Guessing there would mean cutting a
 * real word off every description, so it declines and everything is held to the
 * same day.
 *
 * @param {object[]} transactions
 * @returns {string[]|null} the folded words of the ending, in order
 */
export function nameOf(transactions) {
  const endings = (n) => {
    const counts = new Map()
    for (const tx of transactions ?? []) {
      const parts = words(tx?.description)
      if (parts.length <= n) continue
      const tail = parts.slice(-n)
      // A number is a reference, not a name, and two letters is not a surname.
      if (tail.some((word) => word.length < 3 || /^[0-9]+$/.test(word))) continue
      const key = tail.join(' ')
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }
    let best = null
    for (const [key, count] of counts) if (!best || count > best.count) best = { key, count }
    return best
  }

  const base = endings(1)
  if (!base || base.count < NAME_CONFIDENCE) return null

  let chosen = base
  for (let n = 2; n <= MAX_NAME_WORDS; n++) {
    const longer = endings(n)
    // Only a longer ending that is essentially the *same* ending: a tail seen
    // half as often is a coincidence hanging off the front of a real name.
    if (!longer || !longer.key.endsWith(base.key)) break
    if (longer.count < Math.max(NAME_CONFIDENCE, base.count * 0.8)) break
    chosen = longer
  }
  return chosen.key.split(' ')
}

/** Takes the name off the end, so both halves of a pair read the same. */
export const withoutName = (description, name) => {
  const parts = words(description)
  if (!name || !name.length) return parts.join(' ')
  const tail = parts.slice(-name.length).join(' ')
  return tail === name.join(' ') ? parts.slice(0, -name.length).join(' ') : parts.join(' ')
}

/** Whether this description already carries the name - that is, has settled. */
export const carriesName = (description, name) => {
  if (!name || !name.length) return false
  const parts = words(description)
  return parts.length > name.length && parts.slice(-name.length).join(' ') === name.join(' ')
}

/** Money leaving at an ATM — the rows whose description is rewritten on settling. */
const atmOut = (row) => noteOf(row) === ATM_NOTE && Number(row?.amount) < 0

/** Settled rows never move, so only the unsettled ones are given any licence. */
export const isUnsettled = (row, name) => {
  if (noteOf(row) !== CARD_NOTE && !atmOut(row)) return false
  return !carriesName(row?.description, name)
}

/**
 * What must agree for two rows to be one payment, the date aside: the amount,
 * and the description without the name — or, for an ATM withdrawal, whose
 * machine is swapped for the name, only its first word.
 */
export const keyOf = (row, name) => {
  const amount = Number(row.amount).toFixed(2)
  if (atmOut(row)) return `${amount}|${ATM_NOTE}|${words(row.description)[0] ?? ''}`
  return `${amount}|${withoutName(row.description, name)}`
}

/**
 * How far an unsettled row may have moved.
 *
 * The note and the name are what decide *whether* a row could have moved; this
 * only stops a card payment being matched to an identical one months later. It
 * is deliberately generous, because it is a bound and not the test.
 */
export const SETTLES_WITHIN_DAYS = 14

export default {
  id: 'intesa',
  // Intesa's sheets are read the same way any other is; what it knows that the
  // generic importer does not is about settling, not about columns.
  read: (rows, options) => readStatement(rows, options),
  matchRule(existing) {
    const name = nameOf(existing)
    return {
      withinDays: SETTLES_WITHIN_DAYS,
      // The name Intesa appends on settling is not part of what the payment is.
      keyOf: (row) => keyOf(row, name),
      lenientWhen: (row) => isUnsettled(row, name),
      // The same question, asked by the import rather than by the matching: a
      // row that arrives unsettled is stored as unsettled.
      unsettled: (row) => isUnsettled(row, name),
      name,
    }
  },
}
